/**
 * Showtime V2 - Product JavaScript
 *
 * Product-specific functionality:
 * - Product form (variant selection, price updates, availability)
 * - Product gallery (thumbnail navigation, media switching)
 *
 * Only loaded on product pages to reduce unused JS.
 * Progressive enhancement only - core functionality works without JS.
 */

(function() {
  'use strict';

  // Get shared utilities from global.js
  const { announce } = window.ShowtimeTheme || {};

  // Fallback announce if global not loaded yet
  const _announce = announce || function(message) {
    const region = document.getElementById('aria-live-region');
    if (region) {
      region.textContent = message;
      setTimeout(() => { region.textContent = ''; }, 1000);
    }
  };

  const formatMoney = (value) => {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function' && typeof value === 'number') {
      return window.Shopify.formatMoney(value);
    }
    return '$' + (value / 100).toFixed(2);
  };

  const handleize = (value) => {
    if (!value) return '';
    return value
      .toString()
      .trim()
      .toLowerCase()
      .replace(/['"]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  /**
   * Product Form Handler
   * Handles variant selection, price updates, and availability
   */
  class ProductForm {
    constructor(form) {
      this.form = form;
      this.variantSelects = form.querySelectorAll('.main-product__option-select');
      this.variantIdInput = form.querySelector('[data-variant-id]');
      this.submitButton = form.querySelector('[data-add-to-cart]');
      this.submitButtonText = form.querySelector('[data-add-to-cart-text]');
      this.priceContainer = form.closest('.main-product__info')?.querySelector('[data-product-price]');
      this.inventoryContainer = form.closest('.main-product__info')?.querySelector('[data-inventory-status]');
      this.dynamicCheckout = form.querySelector('[data-dynamic-checkout]');
      this.makeOfferButton = form.closest('.main-product__info')?.querySelector('[data-contact-trigger][data-contact-mode="offer"]');
      this.section = form.closest('.section-main-product') || form.closest('section');
      this.dataHost = this.section?.querySelector('.main-product') || this.section;

      this.addToCartTextAvailable = form.dataset.addToCartText || 'Add to cart';
      this.addToCartTextSoldOut = form.dataset.soldOutText || 'Sold out';
      this.priceLabels = {
        retail: form.dataset.priceLabelRetail || 'Retail Value',
        our: form.dataset.priceLabelOur || 'Our Price',
        price: form.dataset.priceLabelPrice || 'Price'
      };
      this.inventoryLabels = {
        only: form.dataset.inventoryOnlyLabel || 'ONLY',
        left: form.dataset.inventoryLeftLabel || 'LEFT IN STOCK',
        inStock: form.dataset.inventoryInstockLabel || 'IN STOCK',
        backorder: form.dataset.inventoryBackorderLabel || 'AVAILABLE FOR BACKORDER'
      };
      this.inventoryConfig = {
        urgencyEnabled: form.dataset.inventoryUrgencyEnabled !== 'false',
        onlyOneEnabled: form.dataset.inventoryOnlyOneEnabled !== 'false',
        lowThreshold: parseInt(form.dataset.inventoryLowThreshold, 10) || 5
      };
      this.isAuction = form.dataset.isAuction === 'true';
      this.colorOptionIndex = Number.isNaN(parseInt(this.dataHost?.dataset.colorOptionIndex, 10))
        ? null
        : parseInt(this.dataHost?.dataset.colorOptionIndex, 10);

      this.variantSwatchButtons = Array.from(
        (this.section && this.section.querySelectorAll('[data-variant-swatch-button]')) || []
      );

      // Get product data
      const productJsonEl = form.closest('section')?.querySelector('[data-product-json]');
      if (productJsonEl) {
        try {
          this.product = JSON.parse(productJsonEl.textContent);
        } catch (e) {
          console.error('Failed to parse product JSON:', e);
        }
      }

      // Quantity controls
      this.quantityInput = form.querySelector('[data-quantity-input]');
      this.quantityMinus = form.querySelector('[data-quantity-minus]');
      this.quantityPlus = form.querySelector('[data-quantity-plus]');

      this.init();
    }

    init() {
      // Variant selection
      this.variantSelects.forEach(select => {
        select.addEventListener('change', () => this.onVariantChange());
      });

      if (this.variantSwatchButtons.length) {
        this.variantSwatchButtons.forEach(button => {
          button.addEventListener('click', (event) => {
            event.preventDefault();
            const variantId = parseInt(button.dataset.variantId, 10);
            if (!this.product || !variantId) return;
            const variant = this.product.variants.find(item => item.id === variantId);
            if (!variant) return;
            this.variantOverrides = {
              inventoryQty: button.dataset.variantQty,
              available: button.dataset.variantAvailable
            };
            this.variantSelects.forEach((select, index) => {
              if (variant.options[index] !== undefined) {
                select.value = variant.options[index];
              }
            });
            this.onVariantChange();
          });
        });
      }

      // Quantity controls
      if (this.quantityMinus && this.quantityInput) {
        this.quantityMinus.addEventListener('click', () => this.updateQuantity(-1));
      }
      if (this.quantityPlus && this.quantityInput) {
        this.quantityPlus.addEventListener('click', () => this.updateQuantity(1));
      }
    }

    updateQuantity(change) {
      const currentValue = parseInt(this.quantityInput.value) || 1;
      const newValue = Math.max(1, currentValue + change);
      this.quantityInput.value = newValue;
    }

    onVariantChange() {
      if (!this.product) return;

      const selectedOptions = Array.from(this.variantSelects).map(select => select.value);
      const variant = this.findVariant(selectedOptions);

      if (variant) {
        this.updateVariantId(variant);
        this.updatePrice(variant);
        this.updateInventory(variant);
        this.updateAvailability(variant);
        this.updateSoldMessage(variant);
        this.updateSku(variant);
        this.updateVariantSwatches(variant);
        this.updateURL(variant);
        this.announceVariantChange(variant);

        if (this.section) {
          const swatchButton = this.variantSwatchButtons.find(
            (button) => parseInt(button.dataset.variantId, 10) === variant.id
          );
          const variantAlt = swatchButton ? swatchButton.dataset.variantAlt : null;
          this.section.dispatchEvent(new CustomEvent('variant:change', { detail: { variant, variantAlt } }));
        }
      }
    }

    findVariant(selectedOptions) {
      return this.product.variants.find(variant => {
        return variant.options.every((option, index) => option === selectedOptions[index]);
      });
    }

    updateVariantId(variant) {
      if (this.variantIdInput) {
        this.variantIdInput.value = variant.id;
      }
    }

    updatePrice(variant) {
      if (!this.priceContainer) return;

      if (!variant.available) {
        this.priceContainer.hidden = true;
        return;
      }

      this.priceContainer.hidden = false;
      let priceHtml = '';

      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        const savings = variant.compare_at_price - variant.price;
        const savePercent = Math.round((savings * 100) / variant.compare_at_price);
        priceHtml = `
          <div class="price-display price-display--on-sale">
            <div class="price-display__columns">
              <div class="price-display__retail">
                <span class="price-display__label">${this.priceLabels.retail}</span>
                ${
                  this.isAuction
                    ? `<span class="price-display__amount price-display__amount--auction">${formatMoney(variant.compare_at_price)}</span>`
                    : `<span class="price-display__amount price-display__amount--compare"><s>${formatMoney(variant.compare_at_price)}</s></span>`
                }
              </div>
              ${
                this.isAuction
                  ? ''
                  : `<div class="price-display__current">
                      <span class="price-display__label">${this.priceLabels.our}</span>
                      <span class="price-display__amount price-display__amount--sale">${formatMoney(variant.price)}</span>
                    </div>`
              }
            </div>
            ${
              this.isAuction || savePercent <= 0
                ? ''
                : `<div class="price-display__savings">
                    <span class="price-display__savings-percent">You Save: ${savePercent}%</span>
                  </div>`
            }
          </div>
        `;
      } else {
        priceHtml = `
          <div class="price-display">
            <div class="price-display__columns price-display__columns--single">
              <div class="price-display__current price-display__current--full">
                <span class="price-display__label">${this.priceLabels.our}</span>
                <span class="price-display__amount price-display__amount--sale">${formatMoney(variant.price)}</span>
              </div>
            </div>
          </div>
        `;
      }

      this.priceContainer.innerHTML = priceHtml;
    }

    updateAvailability(variant) {
      if (!this.submitButton || !this.submitButtonText) return;

      if (variant.available) {
        this.submitButton.disabled = false;
        this.submitButtonText.textContent = this.addToCartTextAvailable;
      } else {
        this.submitButton.disabled = true;
        this.submitButtonText.textContent = this.addToCartTextSoldOut;
      }

      if (this.priceContainer) {
        this.priceContainer.hidden = !variant.available;
      }
      if (this.dynamicCheckout) {
        this.dynamicCheckout.hidden = !variant.available;
      }
      if (this.makeOfferButton) {
        this.makeOfferButton.hidden = !variant.available;
        this.makeOfferButton.dataset.variantId = variant.id;
        this.makeOfferButton.dataset.variantTitle = variant.title || '';
        this.makeOfferButton.dataset.sku = variant.sku || '';
      }
    }

    updateInventory(variant) {
      if (!this.inventoryContainer) return;

      if (!variant.available || !this.inventoryConfig.urgencyEnabled) {
        this.inventoryContainer.hidden = true;
        this.inventoryContainer.innerHTML = '';
        return;
      }

      let qty = variant.inventory_quantity;
      if (typeof qty === 'string') {
        qty = parseInt(qty, 10);
      }

      if (!Number.isFinite(qty)) {
        const overrideQty = this.variantOverrides?.inventoryQty;
        if (overrideQty !== undefined && overrideQty !== '') {
          qty = parseInt(overrideQty, 10);
        }
      }

      if (!Number.isFinite(qty)) {
        this.inventoryContainer.hidden = false;
        this.inventoryContainer.innerHTML = `
          <div class="inventory-status inventory-status--available">
            <div class="inventory-status__stock">
              <span class="inventory-status__qty" data-inventory-qty></span>
              <span class="inventory-status__text">${this.inventoryLabels.inStock}</span>
            </div>
          </div>
        `;
        return;
      }
      let inventoryHtml = '';

      if (qty < 1) {
        inventoryHtml = `
          <div class="inventory-status inventory-status--backorder">
            <div class="inventory-status__stock">
              <span class="inventory-status__qty" data-inventory-qty>0</span>
              <span class="inventory-status__text">${this.inventoryLabels.left}</span>
            </div>
            <span class="inventory-status__backorder">${this.inventoryLabels.backorder}</span>
          </div>
        `;
      } else if (qty === 1 && this.inventoryConfig.onlyOneEnabled) {
        inventoryHtml = `
          <div class="inventory-status inventory-status--urgent">
            <div class="inventory-status__stock">
              <span class="inventory-status__prefix">${this.inventoryLabels.only}</span>
              <span class="inventory-status__qty" data-inventory-qty>1</span>
              <span class="inventory-status__text">${this.inventoryLabels.left}</span>
            </div>
          </div>
        `;
      } else if (qty <= this.inventoryConfig.lowThreshold) {
        inventoryHtml = `
          <div class="inventory-status inventory-status--low">
            <div class="inventory-status__stock">
              <span class="inventory-status__prefix">${this.inventoryLabels.only}</span>
              <span class="inventory-status__qty" data-inventory-qty>${qty}</span>
              <span class="inventory-status__text">${this.inventoryLabels.left}</span>
            </div>
          </div>
        `;
      } else {
        inventoryHtml = `
          <div class="inventory-status inventory-status--available">
            <div class="inventory-status__stock">
              <span class="inventory-status__qty" data-inventory-qty>${qty}</span>
              <span class="inventory-status__text">${this.inventoryLabels.inStock}</span>
            </div>
          </div>
        `;
      }

      this.inventoryContainer.hidden = false;
      this.inventoryContainer.innerHTML = inventoryHtml;
    }

    updateVariantSwatches(variant) {
      if (!this.variantSwatchButtons.length) return;

      this.variantSwatchButtons.forEach(button => {
        const isActive = parseInt(button.dataset.variantId, 10) === variant.id;
        button.classList.toggle('main-product__variant-swatch--active', isActive);
        button.setAttribute('aria-checked', isActive ? 'true' : 'false');
      });
    }

    updateSku(variant) {
      if (!this.section) return;
      const sku = variant.sku || '';
      const skuEls = this.section.querySelectorAll('[data-product-sku]');
      skuEls.forEach((el) => {
        el.textContent = sku;
      });

      const skuRows = this.section.querySelectorAll('[data-product-sku-row]');
      skuRows.forEach((row) => {
        row.hidden = sku === '';
      });
    }

    updateURL(variant) {
      if (!window.history.replaceState) return;

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    /**
     * Show/hide the one-of-a-kind sold message below the CTA.
     * This element is rendered server-side in main-product.liquid with [data-sold-message].
     */
    updateSoldMessage(variant) {
      const soldMessage = this.form.querySelector('[data-sold-message]');
      if (soldMessage) {
        soldMessage.hidden = variant.available;
        const trigger = soldMessage.querySelector('[data-contact-trigger]');
        if (trigger) {
          trigger.dataset.variantId = variant.id;
          trigger.dataset.variantTitle = variant.title || '';
          trigger.dataset.sku = variant.sku || '';
        }
      }
    }

    /**
     * Announce variant change to screen readers via the aria-live region.
     * Builds a concise message: price + availability status.
     */
    announceVariantChange(variant) {
      const price = formatMoney(variant.price);
      const status = variant.available ? 'Available' : 'Sold';
      _announce(price + '. ' + status + '.');
    }
  }

  /**
   * Product Gallery Handler
   * Handles thumbnail navigation and media switching.
   * Keyboard: Arrow Left/Right to navigate thumbnails, Enter/Space to activate.
   */
  class ProductGallery {
    constructor(section) {
      this.section = section;
      this.dataHost = section.querySelector('.main-product') || section;
      this.mediaContainer = section.querySelector('[data-product-media-container]');
      this.thumbnails = Array.from(section.querySelectorAll('[data-thumbnail-button]'));

      if (!this.mediaContainer || !this.thumbnails.length) return;

      this.activeIndex = 0;
      this.init();
      this.attachVariantListener();
    }

    init() {
      this.thumbnails.forEach((thumbnail, index) => {
        // Click/tap activation (existing behavior)
        thumbnail.addEventListener('click', () => this.activateThumbnail(index));

        // Keyboard: arrow key navigation within the thumbnail list
        thumbnail.addEventListener('keydown', (e) => this.handleKeydown(e, index));
      });

      // Set roving tabindex: only the active thumbnail is in tab order
      this.updateTabindex();

      const initialAlt = this.dataHost.dataset.currentVariantAlt;
      if (initialAlt) {
        this.filterByAlt(initialAlt);
      }
    }

    attachVariantListener() {
      this.section.addEventListener('variant:change', (event) => {
        const variant = event.detail?.variant;
        if (!variant) return;

        if (event.detail?.variantAlt) {
          this.filterByAlt(event.detail.variantAlt);
          return;
        }

        const colorIndex = Number.isNaN(parseInt(this.dataHost.dataset.colorOptionIndex, 10))
          ? null
          : parseInt(this.dataHost.dataset.colorOptionIndex, 10);

        if (colorIndex !== null && variant.options && variant.options[colorIndex]) {
          this.filterByAlt(variant.options[colorIndex]);
        }
      });
    }

    getVisibleIndexes() {
      return this.thumbnails
        .map((thumb, index) => (thumb.hidden ? null : index))
        .filter((index) => index !== null);
    }

    handleKeydown(e, currentIndex) {
      let newIndex = currentIndex;
      const visibleIndexes = this.getVisibleIndexes();
      const currentVisiblePos = visibleIndexes.indexOf(currentIndex);
      const safeVisiblePos = currentVisiblePos === -1 ? 0 : currentVisiblePos;

      if (!visibleIndexes.length) return;

      switch (e.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          e.preventDefault();
          newIndex = visibleIndexes[(safeVisiblePos + 1) % visibleIndexes.length];
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          e.preventDefault();
          newIndex = visibleIndexes[(safeVisiblePos - 1 + visibleIndexes.length) % visibleIndexes.length];
          break;
        case 'Home':
          e.preventDefault();
          newIndex = visibleIndexes[0];
          break;
        case 'End':
          e.preventDefault();
          newIndex = visibleIndexes[visibleIndexes.length - 1];
          break;
        default:
          return; // Let other keys (Enter, Space) use native button behavior
      }

      // Move focus and activate the thumbnail
      this.activateThumbnail(newIndex);
      this.thumbnails[newIndex].focus();
    }

    activateThumbnail(index) {
      this.activeIndex = index;
      const thumbnail = this.thumbnails[index];
      const mediaId = thumbnail.dataset.mediaId;

      // Update active thumbnail styling and aria-selected
      this.thumbnails.forEach((thumb, i) => {
        const isActive = i === index;
        thumb.classList.toggle('main-product__thumbnail--active', isActive);
        thumb.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });

      // Update roving tabindex
      this.updateTabindex();

      // Show corresponding media
      const mediaItems = this.mediaContainer.querySelectorAll('[data-media-id]');
      mediaItems.forEach(item => {
        const isActive = item.dataset.mediaId === mediaId;
        item.hidden = !isActive;
        item.classList.toggle('main-product__media-item--active', isActive);
      });
    }

    /**
     * Roving tabindex: only the active thumbnail is tab-focusable.
     * Others are removed from tab order but reachable via arrow keys.
     */
    updateTabindex() {
      const visibleIndexes = this.getVisibleIndexes();
      if (!visibleIndexes.length) {
        this.thumbnails.forEach((thumb) => {
          thumb.setAttribute('tabindex', '-1');
        });
        return;
      }
      const activeIndex = visibleIndexes.includes(this.activeIndex) ? this.activeIndex : visibleIndexes[0];
      this.thumbnails.forEach((thumb, i) => {
        const isActive = i === activeIndex;
        thumb.setAttribute('tabindex', isActive ? '0' : '-1');
      });
      if (activeIndex !== undefined && activeIndex !== this.activeIndex) {
        this.activeIndex = activeIndex;
      }
    }

    filterByAlt(value) {
      const normalized = handleize(value);

      this.thumbnails.forEach((thumb) => {
        const mediaAlt = handleize(thumb.dataset.mediaAlt || '');
        const matches = !normalized || mediaAlt === normalized || mediaAlt.startsWith(`${normalized}-`);
        thumb.hidden = !matches;
      });

      const mediaItems = this.mediaContainer.querySelectorAll('[data-media-id]');
      mediaItems.forEach((item) => {
        const mediaAlt = handleize(item.dataset.mediaAlt || '');
        const matches = !normalized || mediaAlt === normalized || mediaAlt.startsWith(`${normalized}-`);
        item.hidden = !matches;
      });

      const visibleIndexes = this.getVisibleIndexes();
      if (!visibleIndexes.length && normalized) {
        this.filterByAlt('');
        return;
      }

      if (visibleIndexes.length) {
        this.activateThumbnail(visibleIndexes[0]);
      }
    }
  }

  /**
   * Sticky Mobile Add-to-Cart Bar
   * Shows a fixed bottom bar on mobile when the original add-to-cart
   * button scrolls out of view. Uses IntersectionObserver for performance.
   */
  class StickyAddToCart {
    constructor(section) {
      this.section = section;
      this.submitButton = section.querySelector('[data-add-to-cart]');
      this.stickyBar = section.querySelector('[data-sticky-add-to-cart]');
      this.stickyButton = section.querySelector('[data-sticky-add-to-cart-button]');
      this.stickyPrice = section.querySelector('[data-sticky-price]');

      if (!this.submitButton || !this.stickyBar) return;

      this.mql = window.matchMedia('(max-width: 749px)');
      this.observer = null;
      this.init();
    }

    init() {
      this.stickyButton?.addEventListener('click', () => {
        if (!this.stickyButton.disabled) {
          this.submitButton.click();
        }
      });

      this.section.addEventListener('variant:change', (event) => {
        this.updateFromVariant(event.detail?.variant);
      });

      this.checkMedia();
      this.mql.addEventListener('change', () => this.checkMedia());
    }

    checkMedia() {
      if (this.mql.matches) {
        this.setupObserver();
      } else {
        this.destroyObserver();
        this.stickyBar.hidden = true;
        this.stickyBar.classList.remove('is-visible');
      }
    }

    setupObserver() {
      if (this.observer) return;
      this.stickyBar.hidden = false;

      this.observer = new IntersectionObserver((entries) => {
        const isVisible = entries[0].isIntersecting;
        this.stickyBar.classList.toggle('is-visible', !isVisible);
        document.body.classList.toggle('sticky-bar-visible', !isVisible);
      }, { threshold: 0 });

      this.observer.observe(this.submitButton);
    }

    destroyObserver() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
        document.body.classList.remove('sticky-bar-visible');
      }
    }

    updateFromVariant(variant) {
      if (!variant || !this.stickyButton || !this.stickyPrice) return;

      this.stickyButton.disabled = !variant.available;
      this.stickyButton.textContent = variant.available
        ? (this.submitButton?.querySelector('[data-add-to-cart-text]')?.textContent || 'ADD TO BAG')
        : 'SOLD OUT';

      if (variant.available) {
        let priceHtml = '';
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          priceHtml =
            '<div class="sticky-price__retail">' +
              '<span class="sticky-price__label">Retail</span>' +
              '<span class="sticky-price__amount sticky-price__amount--compare"><s>' + formatMoney(variant.compare_at_price) + '</s></span>' +
            '</div>' +
            '<div class="sticky-price__divider"></div>' +
            '<div class="sticky-price__current">' +
              '<span class="sticky-price__label">Our Price</span>' +
              '<span class="sticky-price__amount sticky-price__amount--sale">' + formatMoney(variant.price) + '</span>' +
            '</div>';
        } else {
          priceHtml =
            '<div class="sticky-price__current sticky-price__current--solo">' +
              '<span class="sticky-price__label">Price</span>' +
              '<span class="sticky-price__amount sticky-price__amount--sale">' + formatMoney(variant.price) + '</span>' +
            '</div>';
        }
        this.stickyPrice.innerHTML = priceHtml;
      } else {
        this.stickyPrice.innerHTML = '';
      }
    }
  }

  /**
   * Recently Viewed Product Tracker
   * Stores product handles in localStorage for recently viewed display.
   * Phase 1: tracking only. Phase 2: rendering product cards.
   */
  class RecentlyViewedTracker {
    constructor(productHandle, productId) {
      this.storageKey = 'castleJewelry_recentlyViewed';
      this.maxItems = 20;
      if (productHandle) {
        this.trackProduct(productHandle, productId);
      }
    }

    trackProduct(handle, id) {
      try {
        var viewed = JSON.parse(localStorage.getItem(this.storageKey) || '[]');
        viewed = viewed.filter(function(item) { return item.handle !== handle; });
        viewed.unshift({ handle: handle, id: id, timestamp: Date.now() });
        viewed = viewed.slice(0, this.maxItems);
        localStorage.setItem(this.storageKey, JSON.stringify(viewed));
      } catch (e) { /* localStorage unavailable */ }
    }

    static getViewed() {
      try {
        return JSON.parse(localStorage.getItem('castleJewelry_recentlyViewed') || '[]');
      } catch (e) {
        return [];
      }
    }
  }

  /**
   * Initialize product components when DOM is ready
   */
  function init() {
    // Product forms
    document.querySelectorAll('[data-product-form]').forEach(form => {
      new ProductForm(form);
    });

    // Product galleries
    document.querySelectorAll('.section-main-product').forEach(section => {
      new ProductGallery(section);
    });

    // Sticky mobile add-to-cart
    document.querySelectorAll('.section-main-product').forEach(section => {
      new StickyAddToCart(section);
    });

    // Recently viewed tracker
    var productJsonEl = document.querySelector('[data-product-json]');
    if (productJsonEl) {
      try {
        var product = JSON.parse(productJsonEl.textContent);
        if (product && product.handle) {
          new RecentlyViewedTracker(product.handle, product.id);
        }
      } catch (e) { /* skip */ }
    }
  }

  // Expose RecentlyViewedTracker for use by other sections
  window.RecentlyViewedTracker = RecentlyViewedTracker;

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
